import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UserPlus, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import portalApi from '../../utils/portalApi';

const PortalRegister = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    document: '',
    address: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error('Nombre y teléfono son obligatorios');
      return;
    }
    setLoading(true);
    try {
      await portalApi.post('/portal/register', form);
      setSuccess(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error en el registro');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md bg-[#0A0A0A] border border-white/10 p-8 md:p-10 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mb-6">
            <CheckCircle2 className="w-8 h-8 text-emerald-400" />
          </div>
          <h1 className="font-['Playfair_Display'] text-3xl mb-3">¡Registro recibido!</h1>
          <p className="text-sm text-white/60 mb-2">
            Hola <span className="text-white">{form.name}</span>,
          </p>
          <p className="text-sm text-white/60 mb-8 leading-relaxed">
            Hemos recibido tu solicitud. El administrador te enviará tu <span className="text-white font-semibold">código de acceso personal</span> por WhatsApp al número que registraste.
          </p>
          <button
            onClick={() => navigate('/portal/login')}
            data-testid="portal-go-login"
            className="w-full bg-white text-black hover:bg-gray-200 transition-colors px-6 py-3 text-xs uppercase tracking-[0.2em] font-semibold mb-3"
          >
            Ir a iniciar sesión
          </button>
          <Link
            to="/"
            className="inline-block text-xs uppercase tracking-[0.2em] text-white/40 hover:text-white"
          >
            Volver al inicio
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/50 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft className="w-3 h-3" />
          Volver al inicio
        </Link>

        <div className="bg-[#0A0A0A] border border-white/10 p-8 md:p-10">
          <div className="flex justify-center mb-6">
            <div className="w-14 h-14 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
              <UserPlus className="w-6 h-6 text-white" />
            </div>
          </div>
          <h1 className="font-['Playfair_Display'] text-3xl text-center mb-2">Crear cuenta</h1>
          <p className="text-center text-sm text-white/50 mb-8">
            Regístrate como cliente. Recibirás tu código por WhatsApp.
          </p>

          <form onSubmit={handleSubmit} data-testid="portal-register-form" className="space-y-4">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] mb-2 text-white/60">Nombre completo *</label>
              <input
                type="text"
                value={form.name}
                onChange={handleChange('name')}
                required
                data-testid="register-name"
                className="w-full bg-[#121212] border border-white/10 focus:border-white/30 px-4 py-3 text-white text-sm outline-none"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] mb-2 text-white/60">Teléfono / WhatsApp *</label>
              <input
                type="tel"
                value={form.phone}
                onChange={handleChange('phone')}
                required
                placeholder="+57 300 123 4567"
                data-testid="register-phone"
                className="w-full bg-[#121212] border border-white/10 focus:border-white/30 px-4 py-3 text-white text-sm outline-none"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] mb-2 text-white/60">Email (opcional)</label>
              <input
                type="email"
                value={form.email}
                onChange={handleChange('email')}
                data-testid="register-email"
                className="w-full bg-[#121212] border border-white/10 focus:border-white/30 px-4 py-3 text-white text-sm outline-none"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] mb-2 text-white/60">Cédula / NIT (opcional)</label>
              <input
                type="text"
                value={form.document}
                onChange={handleChange('document')}
                data-testid="register-document"
                className="w-full bg-[#121212] border border-white/10 focus:border-white/30 px-4 py-3 text-white text-sm outline-none"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] mb-2 text-white/60">Dirección (opcional)</label>
              <input
                type="text"
                value={form.address}
                onChange={handleChange('address')}
                data-testid="register-address"
                className="w-full bg-[#121212] border border-white/10 focus:border-white/30 px-4 py-3 text-white text-sm outline-none"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="register-submit"
              className="w-full bg-white text-black hover:bg-gray-200 transition-colors duration-300 px-6 py-4 text-xs uppercase tracking-[0.2em] font-semibold disabled:opacity-50 mt-2"
            >
              {loading ? 'Enviando...' : 'Enviar solicitud'}
            </button>
          </form>

          <p className="text-center text-xs text-white/40 mt-6">
            ¿Ya tienes código?{' '}
            <Link to="/portal/login" className="text-white underline">
              Inicia sesión
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PortalRegister;
